/**
 * Ticket-screen Contextual Brain — "this customer" (frozen י״א.11).
 * Layer-Law answer: an Insight-Panel INSTANCE of the Brain layer's customer
 * projections; reads GET /ai/brain/context/contact/:contactId only (the
 * Slice-6 resolvers verbatim). Honesty: absent leadScore renders as
 * "not scored yet" — never a fabricated number (5.5.1 ruling).
 */
import React, { useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography } from "@material-ui/core";
import InsightPanel from "../components/InsightPanel";
import { StatBadge, KeyValueRow, EmptyState } from "../components/core";
import { fetchContactContext } from "../aiosApi";
import { aiosT } from "../strings";

const useStyles = makeStyles(() => ({
  chips: { display: "flex", gap: "var(--aios-space-xs)", flexWrap: "wrap" },
  small: { fontSize: 12, color: "var(--aios-text-muted)", overflowWrap: "anywhere" },
  item: { fontSize: 12.5, color: "var(--aios-text-secondary)", overflowWrap: "anywhere" },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "var(--aios-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: 0.3,
    marginBlockStart: 6,
  },
}));

const stageLabel = stage => aiosT(`stage_${stage}`);
const intentLabel = intent => aiosT(`intent_${intent}`);

const TicketInsight = ({ contactId }) => {
  const classes = useStyles();
  const fetcher = useCallback(() => fetchContactContext(contactId), [contactId]);
  if (!contactId) return null;
  return (
    <InsightPanel
      title={aiosT("insightTicketTitle")}
      subtitle={aiosT("insightTicketSubtitle")}
      footnote={aiosT("shadowNote")}
      fetcher={fetcher}
      render={data => {
        if (!data.found || !data.intelligence) {
          // "No Brain data for this customer yet" restated the title. The real cause is
          // that identity resolution did not return a person — which also happens when
          // the contact belongs to a different account, and that is worth saying.
          return (
            <EmptyState
              title={aiosT("insightTicketTitle")}
              reason={aiosT("contactNotResolved")}
            />
          );
        }
        const intel = data.intelligence;
        return (
          <Box display="flex" flexDirection="column" gridGap={6}>
            <Box className={classes.chips}>
              <StatBadge variant="brand">
                {aiosT("journeyLabel")}: {stageLabel(intel.journeyStage)}
              </StatBadge>
              <StatBadge
                variant={
                  intel.buyingIntent === "hot"
                    ? "ok"
                    : intel.buyingIntent === "warm"
                    ? "warn"
                    : "muted"
                }
              >
                {aiosT("intentLabel")}: {intentLabel(intel.buyingIntent)}
              </StatBadge>
              {/* intel.sentiment is SENTIMENT_PLACEHOLDER ("neutral") for EVERY
                  contact — deriveCustomerIntelligence.ts:28,202 — and its own
                  docblock forbids reading it as an analysis that was performed.
                  Inside a panel subtitled "Live intelligence picture" that read
                  as an observation. State the absence, same as leadUnscored. */}
              <StatBadge variant="muted">
                {aiosT("sentimentLabel")}: {aiosT("sentimentNotDerived")}
              </StatBadge>
            </Box>

            <KeyValueRow
              label={aiosT("leadScoreLabel")}
              value={
                intel.leadScore
                  ? `${intel.leadScore.value ?? intel.leadScore.score ?? "—"}`
                  : aiosT("leadUnscored")
              }
            />

            {Array.isArray(intel.missingInfo) && intel.missingInfo.length > 0 && (
              <>
                <Typography className={classes.sectionTitle}>
                  {aiosT("missingInfoTitle")}
                </Typography>
                {intel.missingInfo.slice(0, 5).map(missing => (
                  <Typography key={missing.key} className={classes.item}>
                    • {missing.label || missing.key}
                    {missing.blocksStage
                      ? ` (${aiosT("blocksStagePrefix")} ${stageLabel(missing.blocksStage)})`
                      : ""}
                  </Typography>
                ))}
              </>
            )}

            {Array.isArray(intel.nextBestQuestions) &&
              intel.nextBestQuestions.length > 0 && (
                <>
                  <Typography className={classes.sectionTitle}>
                    {aiosT("nextQuestionTitle")}
                  </Typography>
                  {intel.nextBestQuestions.slice(0, 2).map((question, index) => (
                    <Typography key={index} className={classes.item}>
                      • {question.question}
                    </Typography>
                  ))}
                </>
              )}

            {Array.isArray(intel.intake) && intel.intake.length > 0 && (
              <>
                <Typography className={classes.sectionTitle}>
                  {aiosT("intakeTitle")}
                </Typography>
                {intel.intake.slice(0, 5).map((entry, index) => (
                  <Typography key={index} className={classes.item}>
                    {entry.key}: {entry.value}{" "}
                    <span className={classes.small}>({entry.source})</span>
                  </Typography>
                ))}
              </>
            )}

            <Typography className={classes.sectionTitle}>
              {aiosT("confirmedMemoryTitle")} ({data.confirmedMemoryCount})
            </Typography>
            {(Array.isArray(data.memoryPreview) ? data.memoryPreview : []).map((item, index) => (
              <Typography key={index} className={classes.item}>
                {item.key}: {item.valuePreview}
              </Typography>
            ))}

            <Typography className={classes.small}>
              {aiosT("evidenceLabel")}: {(intel.evidenceRefs || []).length}
            </Typography>
          </Box>
        );
      }}
    />
  );
};

export default TicketInsight;
